import {collection, onSnapshot, type Unsubscribe} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import {AccountToken, AccountTokenProvider} from '../__generated__/resolvers-types';
import {firebaseAuth, firebaseFirestore} from '../setup/firebase';

/**
 * Account detail for etsy
 */
export type HookAccountToken = Pick<AccountToken, 'id' | 'name' | 'provider' | 'photoURL' | 'shopId' | 'userId'>;

export default function useGetAllAccountToken() {
  const [uid, setUid] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<HookAccountToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<HookAccountToken | undefined>();

  useEffect(() => {
    const unsubAuth = firebaseAuth.onAuthStateChanged((user) => {
      setUid(user?.uid ?? null);
    });
    return () => {
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    if (!uid) {
      setAccounts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const collRef = collection(firebaseFirestore, 'user', uid, 'accountTokens');
    const unsub: Unsubscribe = onSnapshot(
      collRef,
      (snap) => {
        const list: HookAccountToken[] = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          list.push({
            id: d.id,
            name: typeof data.name === 'string' ? data.name : '',
            provider: (data.provider as AccountTokenProvider) ?? AccountTokenProvider.ETSY,
            photoURL: typeof data.photoURL === 'string' ? data.photoURL : undefined,
            shopId: typeof data.shopId === 'number' ? data.shopId : undefined,
            userId: typeof data.userId === 'number' ? data.userId : 0,
          });
        });
        setAccounts(list);
        setLoading(false);
      },
      (err) => {
        console.error('useGetAllAccountToken onSnapshot error', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [uid]);

  const refetchAccounts = async () => {
    // No longer required as onSnapshot provides real-time updates
  };

  return {
    accounts,
    refetchAccounts,
    selectedAccount,
    setSelectedAccount,
    loading,
  };
}
